import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowUpRight, ShieldCheck } from "lucide-react";
import { LogoMark } from "@/components/Logo";

export default function Footer() {
  return (
    <footer className="bg-navy-deep text-white relative">
      <div className="border-b border-white/10" data-testid="footer-risk-warning">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-5 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-amber shrink-0 mt-0.5" strokeWidth={2.4} />
          <p className="text-[13px] leading-relaxed text-white/80">
            <span className="font-bold text-white">Business finance disclosure:</span> All lending is subject to status, security and affordability. Missed repayments may result in additional fees, recovery action and impact your business credit file.
          </p>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-5 md:px-8 pt-20 pb-10">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-4">
            <Link to="/" className="inline-flex items-center gap-2.5 mb-5">
              <LogoMark size={44} />
              <div className="font-display font-extrabold text-[22px] tracking-[-0.035em]">
                Swift<span className="text-emerald">Cash</span>
              </div>
            </Link>
            <p className="text-[14.5px] text-white/70 leading-[1.65] font-medium max-w-sm">
              UK business finance — from £5k working capital to £500M growth deals. Whole-of-market panel, indicative terms in hours.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 h-9 px-4 rounded-full bg-emerald/10 border border-emerald/25 text-emerald text-[12px] font-bold">
              <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2.4} />
              UK Registered · Co. No. 17214446
            </div>
          </div>

          <div className="md:col-span-2">
            <Heading>Product</Heading>
            <FL to="/apply">Apply now</FL>
            <FL href="/#pricing">Pricing</FL>
            <FL href="/#how">How it works</FL>
            <FL href="/#faq">FAQ</FL>
          </div>
          <div className="md:col-span-2">
            <Heading>For lenders</Heading>
            <FL to="/lenders">Join the panel</FL>
            <FL to="/lender/signup">Apply as lender</FL>
            <FL to="/lender/login">Lender login</FL>
          </div>
          <div className="md:col-span-2">
            <Heading>Support</Heading>
            <FL to="/apply">Check eligibility</FL>
            <FL href="mailto:hello@swiftca.sh">Contact us</FL>
          </div>
          <div className="md:col-span-2">
            <Heading>Get in touch</Heading>
            <a href="mailto:hello@swiftca.sh" className="block text-[14.5px] font-extrabold text-white hover:text-emerald transition-colors mb-2">
              hello@swiftca.sh
            </a>
            <a href="tel:+442037286215" className="block font-mono text-[13px] text-white/70 hover:text-white mb-4">
              020 3728 6215
            </a>
            <p className="text-[11px] text-white/45 leading-relaxed font-mono uppercase tracking-wider">
              Mon–Fri · 8am–7pm
            </p>
          </div>
        </div>

        <div className="h-px bg-white/10 mb-8" />

        <div className="grid md:grid-cols-2 gap-6 text-[11px] text-white/55 leading-relaxed font-mono mb-8">
          <div>
            <div className="uppercase tracking-[0.18em] text-white/50 mb-2 font-bold">Representative example</div>
            <p className="leading-relaxed">
              Borrow £100,000 over 60 months at 8.9% APR. 60 monthly repayments of £2,068. Total amount payable £124,082. Rate shown is illustrative — final rate is bespoke and depends on turnover, security, sector and trading history.
            </p>
          </div>
          <div>
            <div className="uppercase tracking-[0.18em] text-white/50 mb-2 font-bold">Company information</div>
            <p className="leading-relaxed">
              SwiftCash is a trading style of <span className="text-white font-bold">Connexis Group Ltd</span>, a company registered in England &amp; Wales (Company No. 17214446). Registered office: 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ. All loans are subject to status and affordability checks.
            </p>
          </div>
        </div>

        <div className="rounded-2xl bg-white/[0.03] border border-white/10 px-5 py-5 md:px-7 md:py-6 mb-6">
          <div className="grid md:grid-cols-12 gap-5 items-start">
            <div className="md:col-span-3">
              <div className="font-mono uppercase tracking-[0.18em] text-white/50 text-[10.5px] font-bold mb-2">A SwiftCash company</div>
              <div className="font-display font-extrabold text-white text-[19px] tracking-[-0.02em]">Connexis Group Ltd</div>
            </div>
            <div className="md:col-span-9 grid sm:grid-cols-3 gap-x-6 gap-y-3 text-[12px] text-white/65 font-mono leading-relaxed">
              <div>
                <div className="text-white/45 uppercase tracking-[0.14em] text-[10px] font-bold mb-1">Company number</div>
                <div className="text-white/85">17214446</div>
              </div>
              <div>
                <div className="text-white/45 uppercase tracking-[0.14em] text-[10px] font-bold mb-1">Registered office</div>
                <div className="text-white/85">71-75 Shelton Street, Covent Garden, London, WC2H 9JQ</div>
              </div>
              <div>
                <div className="text-white/45 uppercase tracking-[0.14em] text-[10px] font-bold mb-1">ICO registration</div>
                <div className="text-white/85">ZC148599</div>
              </div>
            </div>
          </div>
        </div>

        <p className="text-[11px] text-white/40 text-center font-mono">
          © {new Date().getFullYear()} Connexis Group Ltd. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

function Heading({ children }) {
  return <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-white/50 mb-5 font-bold">{children}</div>;
}

function FL({ to, href, children, external }) {
  const cls = "group inline-flex items-center gap-1 py-1.5 text-[14px] text-white/75 hover:text-white transition-colors font-semibold";
  if (to) {
    return (
      <Link to={to} className={`${cls} block`}>
        {children}
        <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
      </Link>
    );
  }
  // Internal hash routes like "/#pricing" should use Link to avoid full page reload
  if (href && href.startsWith("/") && !external) {
    return (
      <Link to={href} className={`${cls} block`}>
        {children}
        <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
      </Link>
    );
  }
  const extraProps = external ? { target: "_blank", rel: "noreferrer" } : {};
  return (
    <a href={href} {...extraProps} className={`${cls} block`}>
      {children}
      <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
    </a>
  );
}
